import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowsClockwise, UploadSimple, MagicWand } from "@phosphor-icons/react";

/**
 * Backfill Attribution — admin tool for assigning "stranded" Skimlinks
 * and mrge commissions to the publisher who actually drove them.
 *
 * The need: when a publisher uses Skimlinks JS / SkimScript / a hand-built
 * `go.skimresources.com` URL instead of our `/api/c/{brand_id}` redirect,
 * the conversion arrives with an empty `xcust` so there's no way to tie
 * the commission to a publisher. This page lets admin clean that up:
 *
 *   • Inventory tab — see how much is stranded per merchant.
 *   • By merchant tab — bulk-attribute one merchant → one publisher.
 *   • By CSV tab     — upload Skimlinks/mrge's own attribution CSV
 *                       (order_id → sub_id) for multi-pub merchants.
 *   • History tab    — recent backfill audit log.
 *
 * Both attribute modes default to DRY RUN — admin sees a preview row
 * count before clicking "Apply attribution" to actually persist.
 */
export default function AdminBackfillAttribution() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeNetwork, setActiveNetwork] = useState("skimlinks");

  // Merchant mode state
  const [mCampaignId, setMCampaignId] = useState("");
  const [mSubId, setMSubId] = useState("");
  const [mPreview, setMPreview] = useState(null);
  const [mBusy, setMBusy] = useState(false);

  // CSV mode state
  const [cFile, setCFile] = useState(null);
  const [cPreview, setCPreview] = useState(null);
  const [cBusy, setCBusy] = useState(false);

  // History
  const [historyRows, setHistoryRows] = useState([]);

  // Diagnose
  const [dSubId, setDSubId] = useState("");
  const [dResult, setDResult] = useState(null);
  const [dBusy, setDBusy] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/backfill/stranded-summary");
      setSummary(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load stranded inventory");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await API.get("/admin/backfill/runs?limit=100");
      setHistoryRows(data.runs || []);
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, h] = await Promise.all([
          API.get("/admin/backfill/stranded-summary"),
          API.get("/admin/backfill/runs?limit=100"),
        ]);
        if (!alive) return;
        setSummary(s.data);
        setHistoryRows(h.data?.runs || []);
      } catch (err) {
        if (alive) toast.error(err?.response?.data?.detail || "Failed to load backfill data");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Mode 1 — by merchant
  const runMerchant = async (commit) => {
    if (!mCampaignId || !mSubId) {
      toast.error("Pick a merchant and enter a sub_id");
      return;
    }
    setMBusy(true);
    try {
      const { data } = await API.post("/admin/backfill/attribute-by-merchant", {
        network: activeNetwork,
        campaign_id: mCampaignId,
        sub_id: mSubId.trim().toUpperCase(),
        commit,
      });
      setMPreview(data);
      if (commit) {
        toast.success(`Applied — ${data.totals.updated} action(s) attributed to ${data.publisher.sub_id}`);
        fetchSummary();
        fetchHistory();
      } else {
        toast.info(`Dry-run · would update ${data.totals.updated} action(s)`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed");
    } finally {
      setMBusy(false);
    }
  };

  // Mode 2 — by CSV
  const runCsv = async (commit) => {
    if (!cFile) {
      toast.error("Select a CSV file first");
      return;
    }
    setCBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", cFile);
      fd.append("network", activeNetwork);
      fd.append("commit", commit ? "true" : "false");
      const { data } = await API.post(
        "/admin/backfill/attribute-by-csv",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setCPreview(data);
      if (commit) {
        toast.success(`Applied · ${data.totals.updated} attributed · ${data.totals.skipped} skipped`);
        fetchSummary();
        fetchHistory();
      } else {
        toast.info(`Dry-run · would update ${data.totals.updated} of ${data.csv_rows_read} rows`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV upload failed");
    } finally {
      setCBusy(false);
    }
  };

  // Diagnose
  const runDiagnose = async () => {
    if (!dSubId.trim()) {
      toast.error("Enter a sub_id");
      return;
    }
    setDBusy(true);
    try {
      const { data } = await API.get(`/admin/backfill/diagnose-subid?sub_id=${encodeURIComponent(dSubId.trim().toUpperCase())}&days=90`);
      setDResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Diagnose failed");
    } finally {
      setDBusy(false);
    }
  };

  const netSummary = summary?.networks?.[activeNetwork];

  return (
    <div className="p-8" data-testid="admin-backfill-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
            Ops · Stranded Commissions
          </p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none flex items-center gap-2">
            <MagicWand size={28} weight="bold" /> Backfill Attribution
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-3xl">
            Attribute historical Skimlinks/mrge commissions that arrived with empty <code className="font-mono">xcust</code> to the publisher who actually drove them.
            Use this when a pub bypassed our tracker (Skimlinks JS, SkimScript, or hand-built links). Two modes — pick one per case.
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchSummary(); fetchHistory(); }} disabled={loading} data-testid="backfill-refresh">
          <ArrowsClockwise size={16} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Network picker */}
      <div className="mb-6 flex items-center gap-2" data-testid="backfill-network-row">
        {["skimlinks", "mrge"].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => { setActiveNetwork(n); setMPreview(null); setCPreview(null); }}
            className={`px-4 h-9 text-xs font-bold uppercase tracking-wider rounded-md border ${
              activeNetwork === n
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
            data-testid={`backfill-network-${n}`}
          >
            {n}
          </button>
        ))}
        {netSummary && (
          <div className="ml-4 text-xs text-slate-500">
            <span className="font-bold text-slate-900 font-mono">{netSummary.total_actions.toLocaleString()}</span> stranded actions · <span className="font-bold text-slate-900 font-mono">${netSummary.total_payout.toLocaleString()}</span> commission
          </div>
        )}
      </div>

      <Tabs defaultValue="inventory">
        <TabsList className="mb-6">
          <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="diagnose" data-testid="tab-diagnose">Diagnose sub_id</TabsTrigger>
          <TabsTrigger value="merchant" data-testid="tab-merchant">By merchant</TabsTrigger>
          <TabsTrigger value="csv" data-testid="tab-csv">By CSV upload</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({historyRows.length})</TabsTrigger>
        </TabsList>

        {/* Inventory */}
        <TabsContent value="inventory">
          <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid="inventory-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Campaign ID</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Merchant name</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Stranded actions</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Sale amount</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-emerald-700 font-bold">Commission</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {(netSummary?.merchants || []).map((m) => (
                  <tr key={m.campaign_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`merchant-row-${m.campaign_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{m.campaign_id || "(blank)"}</td>
                    <td className="px-4 py-3 text-slate-900">{m.campaign_name}</td>
                    <td className="px-4 py-3 text-right font-mono">{m.count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-500">${m.total_amount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">${m.total_payout.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => {
                          setMCampaignId(m.campaign_id);
                          document.querySelector('[data-testid="tab-merchant"]')?.click();
                        }}
                        data-testid={`use-merchant-${m.campaign_id}`}
                      >
                        Use →
                      </Button>
                    </td>
                  </tr>
                ))}
                {(netSummary?.merchants || []).length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400 text-sm">No stranded actions for {activeNetwork}. 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Mode 1: by merchant */}
        <TabsContent value="diagnose">
          <div className="border border-slate-200 rounded-md p-6 bg-white max-w-2xl">
            <p className="text-sm text-slate-700 mb-3">
              Trace one sub_id end-to-end — see whether the publisher exists, whether clicks landed,
              whether tracker hits failed, and which Skimlinks/mrge merchants have stranded actions on brands they clicked.
            </p>
            <div className="flex items-center gap-2">
              <Input
                value={dSubId}
                onChange={(e) => setDSubId(e.target.value.toUpperCase())}
                placeholder="e.g. 72LZBW"
                className="h-9 font-mono"
                data-testid="diagnose-sub-id"
                onKeyDown={(e) => { if (e.key === "Enter") runDiagnose(); }}
              />
              <Button onClick={runDiagnose} disabled={dBusy || !dSubId.trim()} className="bg-[#002FA7] hover:bg-[#002089] text-white" data-testid="diagnose-run">
                {dBusy ? "Diagnosing…" : "Diagnose"}
              </Button>
            </div>
          </div>

          {dResult && (
            <div className="mt-6 space-y-4 max-w-4xl" data-testid="diagnose-result">
              {/* User resolution */}
              <div className={`border rounded-md p-4 ${dResult.user.resolved ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"}`}>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-600 mb-2">User resolution</p>
                {dResult.user.resolved ? (
                  <div className="text-sm">
                    <p className="font-bold text-emerald-700">
                      ✓ {dResult.sub_id} resolves to {dResult.user.email}
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                        {dResult.user.matched_kind} sub_id
                      </span>
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      Parent sub_id: <span className="font-mono font-bold">{dResult.user.parent_sub_id}</span>
                      {(dResult.user.slot_sub_ids || []).length > 0 && (
                        <> · Slots: {dResult.user.slot_sub_ids.map((s) => <span key={s} className="font-mono mx-1 inline-block px-1 bg-slate-200 rounded text-[10px]">{s}</span>)}</>
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm font-bold text-rose-700">✗ {dResult.sub_id} doesn&apos;t match any parent or slot sub_id — publisher doesn&apos;t exist (or sub_id was typed wrong)</p>
                )}
              </div>

              {/* Click log */}
              <div className="border border-slate-200 rounded-md p-4 bg-white">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-600 mb-2">
                  Click log · {dResult.click_log.total_ever.toLocaleString()} total clicks · {dResult.click_log.in_window.toLocaleString()} in last {dResult.window_days}d
                </p>
                {dResult.click_log.by_network.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No clicks in window. Pub is either inactive OR bypassing our tracker.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-1 text-slate-500 font-bold">Network</th>
                        <th className="text-right py-1 text-slate-500 font-bold">Clicks</th>
                        <th className="text-right py-1 text-slate-500 font-bold">Converted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dResult.click_log.by_network.map((r) => (
                        <tr key={r.network} className="border-b border-slate-100">
                          <td className="py-1 font-mono">{r.network}</td>
                          <td className="py-1 text-right font-mono">{r.clicks.toLocaleString()}</td>
                          <td className="py-1 text-right font-mono text-emerald-700">{r.converted.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Tracker hits */}
              {dResult.tracker_hits.total_ever > 0 && (
                <div className="border border-slate-200 rounded-md p-4 bg-white">
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-600 mb-2">
                    Tracker hits · {dResult.tracker_hits.total_ever.toLocaleString()} attempted clicks
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {dResult.tracker_hits.by_outcome.map((r) => {
                      const isOk = r.outcome === "ok";
                      return (
                        <span
                          key={r.outcome}
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                            isOk ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          {r.outcome}: <span className="font-bold ml-1">{r.count.toLocaleString()}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Already-attributed actions */}
              <div className="border border-slate-200 rounded-md p-4 bg-white">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-600 mb-2">
                  Actions already attributed to this sub_id · {dResult.actions.total_ever.toLocaleString()} total
                </p>
                {dResult.actions.total_ever === 0 ? (
                  <p className="text-xs text-slate-400 italic">No actions yet — backfill candidates below if any.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-1 text-slate-500 font-bold">Network</th>
                        <th className="text-right py-1 text-slate-500 font-bold">Count</th>
                        <th className="text-right py-1 text-emerald-700 font-bold">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dResult.actions.by_network.map((r) => (
                        <tr key={r.network} className="border-b border-slate-100">
                          <td className="py-1 font-mono">{r.network}</td>
                          <td className="py-1 text-right font-mono">{r.count.toLocaleString()}</td>
                          <td className="py-1 text-right font-mono text-emerald-700">${r.payout.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Likely backfill candidates */}
              <div className="border border-amber-200 rounded-md p-4 bg-amber-50/40" data-testid="diagnose-candidates">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-amber-700 mb-2">
                  ⚠️ Likely backfill candidates · {dResult.likely_backfill_candidates.length} merchant(s)
                </p>
                <p className="text-[11px] text-slate-600 mb-2">
                  These are stranded Skimlinks/mrge merchants on brands this pub clicked. High chance they belong to {dResult.user.email || dResult.sub_id}.
                </p>
                {dResult.likely_backfill_candidates.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No stranded actions on this pub&apos;s clicked brands. 🎉</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-amber-200">
                        <th className="text-left py-1 text-slate-600 font-bold">Network</th>
                        <th className="text-left py-1 text-slate-600 font-bold">Merchant</th>
                        <th className="text-right py-1 text-slate-600 font-bold">Stranded</th>
                        <th className="text-right py-1 text-emerald-700 font-bold">Commission</th>
                        <th className="text-right py-1 text-slate-600 font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dResult.likely_backfill_candidates.map((r) => (
                        <tr key={`${r.network}-${r.campaign_id}`} className="border-b border-amber-100">
                          <td className="py-1.5 font-mono uppercase text-[10px]">{r.network}</td>
                          <td className="py-1.5">{r.campaign_name} <span className="text-slate-400 font-mono text-[10px]">({r.campaign_id})</span></td>
                          <td className="py-1.5 text-right font-mono">{r.stranded_count.toLocaleString()}</td>
                          <td className="py-1.5 text-right font-mono font-bold text-emerald-700">${r.stranded_payout.toLocaleString()}</td>
                          <td className="py-1.5 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-[10px]"
                              onClick={() => {
                                setActiveNetwork(r.network);
                                setMCampaignId(r.campaign_id);
                                setMSubId(dResult.sub_id);
                                document.querySelector('[data-testid="tab-merchant"]')?.click();
                              }}
                              data-testid={`diagnose-attribute-${r.campaign_id}`}
                            >
                              Attribute →
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Mode 1: by merchant */}
        <TabsContent value="merchant">
          <div className="grid grid-cols-2 gap-4 max-w-2xl">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Campaign ID</Label>
              <Select value={mCampaignId} onValueChange={setMCampaignId}>
                <SelectTrigger className="h-9 mt-1" data-testid="merchant-campaign-select">
                  <SelectValue placeholder="Pick a stranded merchant…" />
                </SelectTrigger>
                <SelectContent>
                  {(netSummary?.merchants || []).map((m) => (
                    <SelectItem key={m.campaign_id} value={m.campaign_id} data-testid={`opt-merchant-${m.campaign_id}`}>
                      {m.campaign_name} · {m.count.toLocaleString()} action(s) · ${m.total_payout.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">Only merchants with stranded actions on {activeNetwork} are listed.</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Target publisher sub_id</Label>
              <Input
                value={mSubId}
                onChange={(e) => setMSubId(e.target.value.toUpperCase())}
                placeholder="e.g. 0NWQ3H"
                className="h-9 mt-1 font-mono"
                data-testid="merchant-sub-id"
              />
              <p className="text-[10px] text-slate-400 mt-1">Use the publisher&apos;s RedClub sub_id (visible in /admin/publishers).</p>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button variant="outline" onClick={() => runMerchant(false)} disabled={mBusy} data-testid="merchant-dry-run">
              {mBusy ? "Working…" : "Preview (dry-run)"}
            </Button>
            <Button
              onClick={() => runMerchant(true)}
              disabled={mBusy || !mPreview}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="merchant-commit"
            >
              {mBusy ? "Applying…" : "Apply attribution"}
            </Button>
            {!mPreview && <span className="text-[11px] text-slate-400 italic">Run a dry-run first to see the impact.</span>}
          </div>

          {mPreview && (
            <div className="mt-6 border border-emerald-200 rounded-md bg-emerald-50/40 p-4" data-testid="merchant-preview">
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-emerald-700 mb-2">
                {mPreview.dry_run ? "Dry-run preview" : "Applied"}
              </p>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Publisher</p>
                  <p className="font-bold text-slate-900 mt-0.5">{mPreview.publisher.email}</p>
                  <p className="text-xs text-slate-500 font-mono">{mPreview.publisher.sub_id}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{mPreview.dry_run ? "Would update" : "Updated"}</p>
                  <p className="font-bold text-2xl font-mono text-emerald-700 mt-0.5">{mPreview.totals.updated.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Skipped</p>
                  <p className="font-bold text-2xl font-mono text-slate-500 mt-0.5">{mPreview.totals.skipped.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Click-log linked</p>
                  <p className="font-bold text-2xl font-mono text-slate-900 mt-0.5">{mPreview.totals.click_linked.toLocaleString()}</p>
                </div>
              </div>
              <p className="mt-3 text-[10px] text-slate-500">
                Matched <span className="font-mono">{mPreview.matched_total.toLocaleString()}</span> action(s) across this merchant.
                {mPreview.totals.postbacks_fired > 0 && (
                  <> Fired <span className="font-mono">{mPreview.totals.postbacks_fired}</span> publisher postback(s).</>
                )}
              </p>
            </div>
          )}
        </TabsContent>

        {/* Mode 2: CSV */}
        <TabsContent value="csv">
          <div className="border border-slate-200 rounded-md p-6 bg-white max-w-2xl">
            <p className="text-sm text-slate-700">
              Upload the CSV you exported from <span className="font-bold capitalize">{activeNetwork}</span> with their own attribution.
              Required columns (case-insensitive): <code className="font-mono bg-slate-100 px-1 rounded">order_id</code> and <code className="font-mono bg-slate-100 px-1 rounded">sub_id</code>.
              Alternative: <code className="font-mono bg-slate-100 px-1 rounded">action_id</code> + <code className="font-mono bg-slate-100 px-1 rounded">sub_id</code>.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setCFile(e.target.files?.[0] || null); setCPreview(null); }}
                className="flex-1"
                data-testid="csv-file-input"
              />
              <Button variant="outline" onClick={() => runCsv(false)} disabled={cBusy || !cFile} data-testid="csv-dry-run">
                <UploadSimple size={14} className="mr-1.5" />
                {cBusy ? "Reading…" : "Preview"}
              </Button>
              <Button
                onClick={() => runCsv(true)}
                disabled={cBusy || !cPreview}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="csv-commit"
              >
                {cBusy ? "Applying…" : "Apply"}
              </Button>
            </div>
            <p className="mt-2 text-[10px] text-slate-400">Run preview first — commit re-uses the same file you uploaded.</p>
          </div>

          {cPreview && (
            <div className="mt-6 border border-emerald-200 rounded-md bg-emerald-50/40 p-4 max-w-2xl" data-testid="csv-preview">
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-emerald-700 mb-2">
                {cPreview.dry_run ? "Dry-run preview" : "Applied"}
              </p>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">CSV rows</p>
                  <p className="font-bold text-2xl font-mono text-slate-900 mt-0.5">{cPreview.csv_rows_read.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{cPreview.dry_run ? "Would update" : "Updated"}</p>
                  <p className="font-bold text-2xl font-mono text-emerald-700 mt-0.5">{cPreview.totals.updated.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Skipped</p>
                  <p className="font-bold text-2xl font-mono text-slate-500 mt-0.5">{cPreview.totals.skipped.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Click-log linked</p>
                  <p className="font-bold text-2xl font-mono text-slate-900 mt-0.5">{cPreview.totals.click_linked.toLocaleString()}</p>
                </div>
              </div>
              {/* Show first 10 skipped rows so admin can fix their CSV */}
              {cPreview.rows.filter((r) => r.skipped).slice(0, 10).length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2">First 10 skipped rows</p>
                  <ul className="text-[11px] font-mono text-slate-600 space-y-0.5 max-h-40 overflow-y-auto">
                    {cPreview.rows.filter((r) => r.skipped).slice(0, 10).map((r, i) => (
                      <li key={i}>· {r.skip_reason} {r.csv_row && `→ ${JSON.stringify(r.csv_row)}`}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* History */}
        <TabsContent value="history">
          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="backfill-history-table">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">When</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Network</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Action ID</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Old → New sub_id</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Mode</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">By</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-4 py-2 text-xs text-slate-600">{r.timestamp?.slice(0, 19)?.replace("T", " ")}</td>
                    <td className="px-4 py-2 text-xs font-bold uppercase">{r.network}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500 truncate max-w-[280px]">{r.action_id}</td>
                    <td className="px-4 py-2 text-xs">
                      <span className="text-slate-400 italic">{r.old_sub_id1 || "(blank)"}</span>
                      <span className="mx-1">→</span>
                      <span className="font-mono font-bold">{r.new_sub_id1}</span>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider font-bold">
                        {r.mode}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">{r.by}</td>
                  </tr>
                ))}
                {historyRows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400 text-sm">No backfill history yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-8 border border-slate-200 bg-slate-50/60 rounded-md p-4 text-[11px] text-slate-600 max-w-3xl">
        <p className="font-bold text-slate-700 mb-1">💡 CLI alternative</p>
        <p>Same tool is available via SSH for prod-direct usage:</p>
        <pre className="mt-2 font-mono text-[10px] bg-white border border-slate-200 rounded p-2 overflow-x-auto">
{`cd /app/backend
python -m scripts.backfill_attribution inventory
python -m scripts.backfill_attribution by-merchant \\
    --network skimlinks --campaign-id 6360 --sub-id 0NWQ3H
# add --commit to actually persist`}
        </pre>
      </div>
    </div>
  );
}
